<template>
  <div ref="elRef">
    <HgModal
      v-model="showLanguageSaveModal"
      :containerCssClass="$style.modalContainer"
      :closeButtonCssClass="$style.closeButton"
      @close="closeModal"
    >
      <h2 :class="$style.modalHeader">{{ t('languageSaveModal.title') }}</h2>
      <p :class="$style.modalBody">
        {{ t('languageSaveModal.body') }}
      </p>
      <div :class="$style.modalButtonContainer">
        <HgButton theme="primary" @click="closeModal">
          {{ t('languageSaveModal.confirmButton') }}
        </HgButton>
      </div>
    </HgModal>

    <ul v-if="isFooter" :id="id" :class="[$style.mobileWrapper]" :hidden="isMenuHidden">
      <li v-for="{ language, label } in supportedLanguages" :key="language" :class="$style.item">
        <router-link
          :class="[
            $style.link,
            {
              [$style.activeLink]: currentLanguage === language,
            },
          ]"
          :to="getLanguageToLink(language)"
          replace
          @click="onSelect(language)"
        >
          {{ label }}
        </router-link>
      </li>
    </ul>

    <div
      v-else
      :class="[$style.wrapper, $style.isDropdown, { [$style.activeWrapper]: !isMenuHidden }]"
    >
      <button
        type="button"
        :aria-controls="id"
        :class="$style.link"
        :aria-expanded="!isMenuHidden ? 'true' : 'false'"
        @click="toggleDropdown"
      >
        <span>{{ currentLanguage.toLocaleUpperCase() }}</span>
        <span
          ><HgIcon
            :icon="'ChevronDown'"
            :rotation="isMenuHidden ? 0 : 180"
            size="large"
            :class="$style.icon"
          />
        </span>
      </button>
      <ul :id="id" :class="[$style.languageMenu]" :hidden="isMenuHidden">
        <li v-for="{ language, label } in supportedLanguages" :key="language" :class="$style.item">
          <router-link
            :class="[
              $style.link,
              {
                [$style.activeLink]: currentLanguage === language,
              },
            ]"
            :to="getLanguageToLink(language)"
            replace
            @click="onSelect(language)"
          >
            {{ label }}
          </router-link>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { HgButton, HgIcon, HgModal, HgSupportedLanguage } from '@smg-real-estate/domus';
import { computed, nextTick, ref, watch } from 'vue';
import { RouterLink, useRoute } from 'vue-router';

import { useDropdownHandler } from '@/components/Agency/AgencySwitcher/mixins/useDropdownHandler';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const route = useRoute();
const emit = defineEmits(['languageMenuItemClick', 'trackLanguageChange']);
const LOCAL_STORAGE_KEY = 'languageSaveModalShown';
const { isMenuHidden, toggleDropdown, closeDropdown, elRef } = useDropdownHandler();

const { isFooter, currentLanguage, id } = defineProps({
  /**
   * We render language selector in menu footer on mobile devices, which requires some customization
   */
  isFooter: {
    type: Boolean,
    required: false,
    default: false,
  },
  /**
   * The current language of translations.
   */
  currentLanguage: {
    type: String,
    required: true,
  },
  /**
   * Id used for aria controls.
   */
  id: {
    type: String,
    required: true,
  },
});

const oldLanguage = ref(currentLanguage);
const showLanguageSaveModal = ref(false);

const supportedLanguages = computed<Array<{ language: HgSupportedLanguage; label: string }>>(() =>
  [
    HgSupportedLanguage.DE,
    HgSupportedLanguage.FR,
    HgSupportedLanguage.IT,
    HgSupportedLanguage.EN,
  ].map((language) => ({
    language,
    label: language.toLocaleUpperCase(),
  })),
);

watch(
  () => oldLanguage,
  () => {
    // Check if we should show the "Language Save" modal
    const languageSaveModalShown = localStorage.getItem(LOCAL_STORAGE_KEY) as string;
    if (!languageSaveModalShown) nextTick(() => (showLanguageSaveModal.value = true));
  },
);

const closeModal = (): boolean => {
  localStorage.setItem(LOCAL_STORAGE_KEY, 'true');
  return (showLanguageSaveModal.value = false);
};

const onSelect = (language: HgSupportedLanguage): void => {
  closeDropdown();

  emit('languageMenuItemClick', language);
  emit('trackLanguageChange', { oldLanguage: oldLanguage.value, language });
  oldLanguage.value = language;
};

const getLanguageToLink = (language: HgSupportedLanguage) => ({
  name: route.name,
  query: route.query,
  params: {
    ...route.params,
    locale: language,
  },
});
</script>

<style lang="scss" module>
@import '@/assets/variables';

$space-125: 1.25rem;
$dropdown-menu-height: 56px;

.modalContainer {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding: 0;
  overflow-y: auto;

  .modalHeader {
    padding: $space-75 $space-300 $space-75 $space-150;
    margin: 0;
    border-bottom: 1px solid $color-grey-50;

    @include heading-3;
  }

  .modalBody {
    max-width: $modal-content-max-width;
    padding: $space-150 $space-150 $space-300 $space-150;
    margin: 0;
    color: $color-grey-90;

    @include body-3-semi-bold;
  }

  .modalButtonContainer {
    display: flex;
    justify-content: flex-end;
    padding: $space-75 $space-150 0 $space-150;
    margin-bottom: $space-75;
    border-top: 1px solid $color-grey-50;
  }
}

@media (min-width: $viewport-sm-min) {
  .modalContainer {
    width: auto;
    height: auto;
  }
}

.activeWrapper {
  background-color: $color-deep-blue-30;
}

.mobileWrapper {
  @include menu-ul-reset;
  @include menu-item-wrapper-hg;

  & {
    padding: 0;
    margin: 0;
    list-style: none;
  }

  .link {
    @include body-2;

    & {
      box-sizing: border-box;
      display: flex;
      align-items: center;
      height: 100%;
      padding: $space-100;
      color: $color-white;
      text-decoration: none;
      border-radius: 0;
    }
  }

  .item {
    position: relative;
  }

  .activeLink {
    border-bottom: 2px solid $color-sky-80;
  }
}

.wrapper {
  @include menu-item-wrapper-hg;

  & {
    box-sizing: border-box;
    align-items: center;
    height: 35px;
    margin-left: 24px;
    border: 1px solid $color-deep-blue-100;
    border-radius: $border-radius-l;
  }

  .link {
    @include body-2;

    & {
      box-sizing: border-box;
      display: flex;
      align-items: center;
      height: 100%;
      padding: 0 5px 0 10px;
      color: $color-grey-120;
      border-radius: 0;
    }
  }

  .icon {
    display: flex;
    margin-left: $space-25;
  }

  &:hover {
    background-color: $color-deep-blue-30;
  }
}

.languageMenu {
  @include menu-ul-reset;

  & {
    display: inline-flex;
    flex-wrap: wrap;
    height: $dropdown-menu-height;
    margin-bottom: -8px !important;
    border-radius: $border-radius;
  }

  .item {
    position: relative;

    &:hover {
      background-color: $color-deep-blue-30;
    }
  }

  .link {
    padding: $space-100;
    text-decoration: none;
  }

  .isDropdown & {
    @include menu-item-dropdown-hg;

    & {
      right: 0;
      left: auto;
      flex-wrap: nowrap;
      min-width: 0;
      padding: 0 $space-50;
      transform: translate(0, 100%);
    }

    &::before {
      right: 7px;
      left: auto;
    }

    .activeLink {
      border-bottom: 2px solid $color-sky-80;
    }

    @media (min-width: $viewport-lg-min) {
      padding: 0 $space-75;
    }
  }
}

.isDropdown {
  display: none;
}

@media (min-width: $viewport-sm-min) {
  .isDropdown {
    display: inline-flex;
  }
}
</style>
